import { useCallback, useState, useEffect } from 'react';

import { Perspective } from '../types';

import { useRealtimeEventHandler } from '@/realtime/useRealtimeEventHandler';
import {
  EventPerspectiveAddedData,
  EventPerspectiveRemovedData,
  EventPerspectiveThumbnailAddedData,
  EventPerspectiveVodReadyData,
  EventPerspectiveEndedData,
  EventDeletedData,
} from '@/realtime/types';
import { enhancedApi } from '@/api/client';
import { store } from '@/store';

interface UseEventPerspectivesRealtimeParams {
  eventId: string;
  initialPerspectives: Perspective[];
  onEventDeleted?: () => void;
}

/**
 * Hook to handle real-time perspective updates via WebSocket.
 * Subscribes to events:{eventId} topic and updates perspectives list.
 */
export function useEventPerspectivesRealtime({
  eventId,
  initialPerspectives,
  onEventDeleted,
}: UseEventPerspectivesRealtimeParams) {
  const [perspectives, setPerspectives] =
    useState<Perspective[]>(initialPerspectives);

  useEffect(() => {
    console.log('[Realtime] Syncing from initialPerspectives:', initialPerspectives.map(p => ({ id: p.id, streamStatus: p.streamStatus })));
    setPerspectives(initialPerspectives);
  }, [initialPerspectives]);

  const handlePerspectiveAdded = useCallback(
    async (data: EventPerspectiveAddedData) => {
      console.log('[Realtime] perspective_added data:', data);
      const isLivestream = Boolean(data.livestream_id);
      const placeholderPerspective: Perspective = {
        id: data.perspective_post_id.toString(),
        username: 'Loading...',
        viewerCount: 0,
        timestamp: new Date().toISOString(),
        postId: data.perspective_post_id.toString(),
        livestreamId: data.livestream_id || '',
        chatChannelId: '', // Will be populated when full post data is fetched
        userId: 0,
        streamStatus: isLivestream ? 'live' : null,
        postType: isLivestream ? 'livestream' : 'recorded',
        processingState: isLivestream ? undefined : 'pending',
      };

      setPerspectives(prev => [placeholderPerspective, ...prev]);

      try {
        const post = await store
          .dispatch(enhancedApi.endpoints.getPost.initiate(data.perspective_post_id))
          .unwrap();

        if (post) {
          const isLivestream = post.post_type === 'livestream';
          const fullPerspective: Perspective = {
            id: post.id.toString(),
            username: post.username,
            viewerCount: 0,
            thumbnailUrl: post.photo_url_compressed || undefined,
            timestamp: post.timestamp,
            postId: post.id.toString(),
            livestreamId: post.livestream_id || '',
            chatChannelId: post.chat_channel_id || '',
            userId: post.user_id,
            streamStatus: isLivestream ? 'live' : null,
            m3u8Url: post.m3u8_url || undefined,
            postType: isLivestream ? 'livestream' : 'recorded',
            videoUrl: post.video_url || undefined,
            photoUrl: post.photo_url || undefined,
            processingState: post.processing_state || undefined,
          };

          setPerspectives(prev =>
            prev.map(p =>
              p.postId === data.perspective_post_id.toString()
                ? fullPerspective
                : p,
            ),
          );
        }
      } catch (error) {
        console.error(
          '[EventPerspectives] Failed to fetch perspective:',
          error,
        );
      }
    },
    [],
  );

  const handlePerspectiveRemoved = useCallback(
    (data: EventPerspectiveRemovedData) => {
      setPerspectives(prev =>
        prev.filter(p => p.postId !== data.perspective_post_id.toString()),
      );
    },
    [],
  );

  const handleThumbnailAdded = useCallback(
    (data: EventPerspectiveThumbnailAddedData) => {
      const postIdStr = data.perspective_post_id.toString();
      setPerspectives(prev =>
        prev.map(p => {
          if (p.postId !== postIdStr) return p;
          // For recorded posts, thumbnail means photo is ready - clear processing state
          const isRecordedPhoto = p.postType === 'recorded' && !p.videoUrl;
          return {
            ...p,
            thumbnailUrl: data.thumbnail_url,
            processingState: isRecordedPhoto ? undefined : p.processingState,
          };
        }),
      );
    },
    [],
  );

  const handleVodReady = useCallback((data: EventPerspectiveVodReadyData) => {
    const postIdStr = data.perspective_post_id.toString();
    setPerspectives(prev =>
      prev.map(p => {
        if (p.postId !== postIdStr) return p;
        return {
          ...p,
          m3u8Url: data.m3u8_url,
          streamStatus: p.postType === 'livestream' ? 'ended' : p.streamStatus,
          processingState: undefined, // Video processing complete
        };
      }),
    );
  }, []);

  const handlePerspectiveEnded = useCallback(
    (data: EventPerspectiveEndedData) => {
      console.log('[Realtime] perspective_ended received:', data);
      setPerspectives(prev =>
        prev.map(p =>
          p.postId === data.perspective_post_id.toString()
            ? { ...p, streamStatus: 'ended' }
            : p,
        ),
      );
    },
    [],
  );

  const handleEventDeleted = useCallback(
    (data: EventDeletedData) => {
      onEventDeleted?.();
    },
    [onEventDeleted],
  );

  useRealtimeEventHandler({
    topic: eventId ? `events:${eventId}` : null,
    handlers: {
      'event.perspective_added': event =>
        handlePerspectiveAdded(event.data as EventPerspectiveAddedData),
      'event.perspective_removed': event =>
        handlePerspectiveRemoved(event.data as EventPerspectiveRemovedData),
      'event.perspective_thumbnail_added': event =>
        handleThumbnailAdded(event.data as EventPerspectiveThumbnailAddedData),
      'event.perspective_vod_ready': event =>
        handleVodReady(event.data as EventPerspectiveVodReadyData),
      'event.perspective_ended': event =>
        handlePerspectiveEnded(event.data as EventPerspectiveEndedData),
      'event.deleted': event =>
        handleEventDeleted(event.data as EventDeletedData),
    },
    deps: [
      handlePerspectiveAdded,
      handlePerspectiveRemoved,
      handleThumbnailAdded,
      handleVodReady,
      handlePerspectiveEnded,
      handleEventDeleted,
    ],
  });

  return {
    perspectives,
  };
}
